/**
 * Quiz primitives — small building-block components shared by QuizFocus,
 * QuizResults, and TestsHub.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/quiz.jsx (QuestionPill,
 * Choice, StatPill, WrongCard) and tests.jsx (TestsScoreBar).
 *
 * Data model: index-based options (BundleQuizQuestion from @arianna/shared),
 * numeric answers (Record<string, number | null>).
 */

import * as React from 'react'
import { clsx } from 'clsx'
import type { BundleQuizQuestion } from '@arianna/shared'

// ---------------------------------------------------------------------------
// QuestionPill — navigator pill for each question position
// ---------------------------------------------------------------------------

export interface QuestionPillProps {
  /** 1-based display number */
  n: number
  isCurrent: boolean
  isAnswered: boolean
  isSkipped: boolean
  onClick: () => void
}

export function QuestionPill({ n, isCurrent, isAnswered, isSkipped, onClick }: QuestionPillProps) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        /* design handoff: minWidth 38px, height 32px, borderRadius 7px, fontSize 11.5px */
        'min-w-[38px] h-[32px] px-[6px] rounded-[7px] text-[11.5px]',
        'cursor-pointer shrink-0 font-mono transition-colors duration-150',
        isCurrent && 'bg-ar-accent text-white border border-ar-accent font-bold',
        isAnswered &&
          !isCurrent &&
          'bg-accent-bg text-ar-accent border border-accent-soft font-semibold',
        isSkipped &&
          !isCurrent &&
          !isAnswered &&
          'bg-transparent text-gold border border-gold font-semibold',
        !isCurrent &&
          !isAnswered &&
          !isSkipped &&
          'bg-transparent text-ink-3 border border-line font-medium',
      )}
    >
      {n}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Choice — selectable option card within QuizFocus
// ---------------------------------------------------------------------------

export interface ChoiceProps {
  /** Option letter label (A, B, C, D, …) */
  letter: string
  text: string
  selected: boolean
  onClick: () => void
  /**
   * Fires on the browser's native `dblclick` (which arrives AFTER two
   * ordinary `click` events have already fired — `onClick`/`onSelectOption`
   * is idempotent, so no timer is used here to distinguish single vs double
   * click; that would delay the single-click response, which must stay
   * instant). Optional: omit to disable the "select and advance" gesture.
   */
  onDoubleClick?: (() => void) | undefined
}

export function Choice({ letter, text, selected, onClick, onDoubleClick }: ChoiceProps) {
  return (
    <button
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={clsx(
        /* design handoff: gap 18px, px 22px, py 16px, borderRadius 11px, border 1.5px */
        'flex items-start gap-[18px] px-[22px] py-4 w-full',
        'rounded-[11px] cursor-pointer text-left font-sans transition-colors duration-150',
        selected
          ? 'bg-accent-bg border-[1.5px] border-ar-accent shadow-[0_0_0_3px_theme(colors.accent-bg)]'
          : 'bg-surface border-[1.5px] border-line',
      )}
    >
      {/* Letter badge */}
      {/* design handoff: width 30px, height 30px, borderRadius 7px, fontSize 13px */}
      <div
        className={clsx(
          'w-[30px] h-[30px] rounded-[7px] flex items-center justify-center',
          'font-mono text-[13px] font-bold tracking-[0.02em] shrink-0 mt-px',
          selected
            ? 'bg-ar-accent border border-ar-accent text-white'
            : 'bg-bg border border-line-2 text-ink-3',
        )}
      >
        {letter}
      </div>
      {/* Option text */}
      {/* design handoff: fontSize 14.5px */}
      <div
        className={clsx(
          'font-sans text-[14.5px] leading-[1.5] tracking-[-0.005em] flex-1 text-left',
          selected ? 'text-ink' : 'text-ink-2',
        )}
      >
        {text}
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// StatPill — colored dot + count + label (used in QuizResults stat row)
// ---------------------------------------------------------------------------

export interface StatPillProps {
  label: string
  n: number
  /** CSS color value for the dot — passed as inline style since it's a runtime value */
  color: string
}

export function StatPill({ label, n, color }: StatPillProps) {
  return (
    <div className="px-4 py-[10px] bg-surface border border-line rounded-[10px] flex items-center gap-[10px] whitespace-nowrap">
      {/* dot color is a runtime value (green/red/ink-3) — inline style is correct here */}
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <span className="font-mono text-md font-semibold text-ink">{n}</span>
      <span className="font-sans text-xs text-ink-3">{label}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ScoreBar — 4-segment proportional bar (correct / wrong / skipped / empty)
// Used by CompletedCard in TestsHub.
// ---------------------------------------------------------------------------

export interface ScoreBarProps {
  correct: number
  wrong: number
  skipped: number
  total: number
}

export function ScoreBar({ correct, wrong, skipped, total }: ScoreBarProps) {
  if (total === 0) return null
  const w = (n: number) => `${Math.round((n / total) * 100)}%`
  return (
    <div className="flex h-1 rounded-sm overflow-hidden bg-surface-3">
      {correct > 0 && (
        <div
          className="bg-green transition-[width] duration-[400ms]"
          style={{ width: w(correct) }}
        />
      )}
      {wrong > 0 && (
        <div
          className="bg-red transition-[width] duration-[400ms] ml-px"
          style={{ width: w(wrong) }}
        />
      )}
      {skipped > 0 && (
        <div
          className="bg-ink-4 transition-[width] duration-[400ms] ml-px"
          style={{ width: w(skipped) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// KeySourceBadge — TASK-017: provenance of the answer key, shown below the
// rationale in WrongCard/CorrectCard. `question.keySource` is optional
// (absent on legacy bundles ingested before key-resolution existed) —
// rendering nothing in that case is the point: zero visual change for
// legacy/desktop callers that never pass the field. 'evidence' additionally
// shows the citation (`evidenceQuote`) that grounds the key, when present.
// Pill styling reuses the uppercase-label language already used above
// (WrongCard's "Hai scelto"/"Corretta" rows) — ar-accent for a verified
// signal, gold for the same "needs attention" tone QuestionPill already uses
// for a skipped question.
// ---------------------------------------------------------------------------

function KeySourceBadge({
  keySource,
  evidenceQuote,
}: {
  keySource: 'evidence' | 'knowledge'
  evidenceQuote?: string | undefined
}) {
  const isEvidence = keySource === 'evidence'
  return (
    <div className="mt-3 flex flex-col items-start gap-[6px]">
      <span
        className={clsx(
          'inline-flex items-center px-2 py-[3px] rounded-full border text-[9.5px] font-bold uppercase tracking-[0.08em] font-sans',
          isEvidence ? 'border-ar-accent text-ar-accent' : 'border-gold text-gold',
        )}
      >
        {isEvidence ? 'Verificata sul materiale' : 'Chiave non verificata sulle fonti'}
      </span>
      {isEvidence && evidenceQuote != null && evidenceQuote.length > 0 && (
        <p className="italic text-[12.5px] text-ink-3 leading-[1.45] font-serif m-0 px-px">
          “{evidenceQuote}”
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// WrongCard — wrong-answer breakdown card in QuizResults
// ---------------------------------------------------------------------------

export interface WrongCardProps {
  question: BundleQuizQuestion
  /** 0-based index of the option the user selected */
  givenIdx: number
  /** Called when user clicks "Vai al capitolo". If omitted, button is hidden. */
  onGoToChapter?: () => void
}

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F']

export function WrongCard({ question, givenIdx, onGoToChapter }: WrongCardProps) {
  const [showOptions, setShowOptions] = React.useState(false)
  const givenLetter = LETTERS[givenIdx] ?? String(givenIdx)
  const givenText = question.options[givenIdx] ?? ''
  const correctLetter = LETTERS[question.correctOptionIdx] ?? String(question.correctOptionIdx)
  const correctText = question.options[question.correctOptionIdx] ?? ''

  return (
    <div className="p-4 pr-[18px] bg-surface border border-line border-l-[3px] border-l-red rounded-[12px] font-sans">
      {/* Stem + review toggle */}
      {/* design handoff: fontSize 15.5px */}
      <div className="flex items-start gap-3 mb-[14px]">
        <div className="flex-1 text-[15.5px] text-ink leading-[1.45] tracking-[-0.005em] font-serif">
          {question.stem}
        </div>
        <OptionsReviewButton open={showOptions} onToggle={() => setShowOptions((o) => !o)} />
      </div>
      {showOptions && <OptionsPanel question={question} givenIdx={givenIdx} />}

      {/* Answer comparison rows */}
      <div className="flex flex-col gap-[7px]">
        {/* Given (wrong) */}
        {/* design handoff: red-tinted card background */}
        <div
          className="flex items-start gap-3 px-[14px] py-[10px] border rounded-[9px]"
          style={{
            background: 'color-mix(in oklab, var(--red) 8%, var(--surface))',
            borderColor: 'color-mix(in oklab, var(--red) 30%, var(--line))',
          }}
        >
          {/* design handoff: fontSize 9.5px for uppercase label */}
          <span className="text-[9.5px] text-red tracking-[0.08em] uppercase font-bold pt-[3px] shrink-0 w-[70px]">
            Hai scelto
          </span>
          <span className="text-xs font-bold text-red pt-[2px] shrink-0 font-mono">
            {givenLetter}.
          </span>
          <span className="text-[13px] text-ink-2 leading-[1.45] flex-1">{givenText}</span>
        </div>

        {/* Correct */}
        {/* design handoff: green-tinted card background */}
        <div
          className="flex items-start gap-3 px-[14px] py-[10px] border rounded-[9px]"
          style={{
            background: 'color-mix(in oklab, var(--green) 10%, var(--surface))',
            borderColor: 'color-mix(in oklab, var(--green) 30%, var(--line))',
          }}
        >
          {/* design handoff: fontSize 9.5px for uppercase label */}
          <span className="text-[9.5px] text-green tracking-[0.08em] uppercase font-bold pt-[3px] shrink-0 w-[70px]">
            Corretta
          </span>
          <span className="text-xs font-bold text-green pt-[2px] shrink-0 font-mono">
            {correctLetter}.
          </span>
          <span className="text-[13px] text-ink leading-[1.45] flex-1">{correctText}</span>
        </div>
      </div>

      {/* Rationale / explanation */}
      {question.rationale != null && question.rationale.length > 0 && (
        <div className="mt-3 px-[14px] py-[10px] bg-bg border border-line rounded-md font-serif text-base leading-[1.5] text-ink-2 tracking-[-0.005em]">
          <span className="text-ar-accent mr-[6px]">✦</span>
          {question.rationale}
        </div>
      )}

      {/* Key provenance badge (TASK-017) — absent on legacy bundles */}
      {question.keySource != null && (
        <KeySourceBadge keySource={question.keySource} evidenceQuote={question.evidenceQuote} />
      )}

      {/* Go to chapter CTA */}
      {onGoToChapter != null && (
        <button
          onClick={onGoToChapter}
          className="mt-3 px-[13px] py-[7px] rounded-md border border-line bg-surface text-ar-accent text-xs font-medium cursor-pointer font-sans"
        >
          Vai al capitolo →
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// OptionsReviewButton — icon-only "?" toggle for the options review panel
// ---------------------------------------------------------------------------

export function OptionsReviewButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      aria-expanded={open}
      aria-label="Rivedi domanda e opzioni"
      title="Rivedi domanda e opzioni"
      className={clsx(
        'w-[26px] h-[26px] rounded-full flex items-center justify-center shrink-0 cursor-pointer font-mono text-[13px] font-bold transition-colors',
        open
          ? 'bg-ar-accent border border-ar-accent text-white'
          : 'bg-surface border border-line text-ink-3',
      )}
    >
      ?
    </button>
  )
}

// ---------------------------------------------------------------------------
// OptionsPanel — question stem + full option list (correct/given highlighted)
// ---------------------------------------------------------------------------

export interface OptionsPanelProps {
  question: BundleQuizQuestion
  /** indice 0-based della risposta data dall'utente, se presente (evidenzia in rosso i distrattori scelti) */
  givenIdx?: number
}

export function OptionsPanel({ question, givenIdx }: OptionsPanelProps) {
  return (
    <div className="mt-3 px-[14px] py-3 bg-bg border border-line rounded-md">
      <div className="font-serif text-[14px] text-ink leading-[1.45] tracking-[-0.005em] mb-3">
        {question.stem}
      </div>
      <div className="flex flex-col gap-[6px]">
        {question.options.map((opt, i) => {
          const isCorrect = i === question.correctOptionIdx
          const isGiven = givenIdx != null && i === givenIdx && !isCorrect
          return (
            <div
              key={i}
              className="flex items-start gap-3 px-[12px] py-2 border rounded-[8px]"
              style={
                isCorrect
                  ? {
                      background: 'color-mix(in oklab, var(--green) 10%, var(--surface))',
                      borderColor: 'color-mix(in oklab, var(--green) 30%, var(--line))',
                    }
                  : isGiven
                    ? {
                        background: 'color-mix(in oklab, var(--red) 8%, var(--surface))',
                        borderColor: 'color-mix(in oklab, var(--red) 30%, var(--line))',
                      }
                    : undefined
              }
            >
              <span
                className={clsx(
                  'text-xs font-bold pt-[1px] shrink-0 font-mono',
                  isCorrect ? 'text-green' : isGiven ? 'text-red' : 'text-ink-4',
                )}
              >
                {LETTERS[i] ?? String(i)}.
              </span>
              <span
                className={clsx(
                  'text-[13px] leading-[1.45] flex-1',
                  isCorrect ? 'text-ink' : 'text-ink-2',
                )}
              >
                {opt}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CorrectCard — correct-answer card in QuizResults (collapsed by default)
// ---------------------------------------------------------------------------

export interface CorrectCardProps {
  question: BundleQuizQuestion
  onGoToChapter?: () => void
}

export function CorrectCard({ question, onGoToChapter }: CorrectCardProps) {
  const [open, setOpen] = React.useState(false)
  const [showOptions, setShowOptions] = React.useState(false)
  const correctLetter = LETTERS[question.correctOptionIdx] ?? String(question.correctOptionIdx)
  const correctText = question.options[question.correctOptionIdx] ?? ''
  return (
    <div className="p-4 pr-[18px] bg-surface border border-line border-l-[3px] border-l-green rounded-[12px] font-sans">
      {/* Header: collapse toggle + options review button */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-start gap-3 flex-1 text-left bg-transparent border-none p-0 cursor-pointer font-sans"
        >
          <span className="text-[10px] text-ink-4 pt-[3px] shrink-0">{open ? '▾' : '▸'}</span>
          <span className="flex-1 text-[15.5px] text-ink leading-[1.45] tracking-[-0.005em] font-serif">
            {question.stem}
          </span>
          <span className="text-[9.5px] text-green tracking-[0.08em] uppercase font-bold pt-[4px] shrink-0">
            Corretta
          </span>
        </button>
        <OptionsReviewButton open={showOptions} onToggle={() => setShowOptions((o) => !o)} />
      </div>
      {showOptions && <OptionsPanel question={question} />}

      {open && (
        <div className="mt-[14px]">
          {/* Riga risposta corretta */}
          <div
            className="flex items-start gap-3 px-[14px] py-[10px] border rounded-[9px]"
            style={{
              background: 'color-mix(in oklab, var(--green) 10%, var(--surface))',
              borderColor: 'color-mix(in oklab, var(--green) 30%, var(--line))',
            }}
          >
            <span className="text-[9.5px] text-green tracking-[0.08em] uppercase font-bold pt-[3px] shrink-0 w-[70px]">
              Corretta
            </span>
            <span className="text-xs font-bold text-green pt-[2px] shrink-0 font-mono">
              {correctLetter}.
            </span>
            <span className="text-[13px] text-ink leading-[1.45] flex-1">{correctText}</span>
          </div>

          {/* Rationale */}
          {question.rationale != null && question.rationale.length > 0 && (
            <div className="mt-3 px-[14px] py-[10px] bg-bg border border-line rounded-md font-serif text-base leading-[1.5] text-ink-2 tracking-[-0.005em]">
              <span className="text-ar-accent mr-[6px]">✦</span>
              {question.rationale}
            </div>
          )}

          {/* Key provenance badge (TASK-017) — absent on legacy bundles */}
          {question.keySource != null && (
            <KeySourceBadge keySource={question.keySource} evidenceQuote={question.evidenceQuote} />
          )}

          {onGoToChapter != null && (
            <button
              onClick={onGoToChapter}
              className="mt-3 px-[13px] py-[7px] rounded-md border border-line bg-surface text-ar-accent text-xs font-medium cursor-pointer font-sans"
            >
              Vai al capitolo →
            </button>
          )}
        </div>
      )}
    </div>
  )
}
